import { useState, useEffect, useCallback, useRef } from 'react';
import { 
  Box, 
  Paper, 
  Typography, 
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Button,
  Fab,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import { tokens } from '../../theme';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLandCart } from '../../contexts/LandCartContext';
import LeaderboardDialog from '../../components/market/LeaderboardDialog';
import InfoDialog from '../../components/market/InfoDialog';
import TerrainIcon from '@mui/icons-material/Terrain';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import DiamondIcon from '@mui/icons-material/Diamond';
import GrainIcon from '@mui/icons-material/Grain';
import MarketHeader from '../../components/market/MarketHeader';
import LandDeedItem from './LandDeedItem';

const LandMarket = ({ setIsSidebar, isMobile }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { addToLandCart } = useLandCart();
  
  // Dialog states for modals
  const [showLeaderboard, setShowLeaderboard] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  
  // State management
  const [landDeeds, setLandDeeds] = useState([]);
  const [filteredDeeds, setFilteredDeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [visibleCount, setVisibleCount] = useState(50);
  const [addingToCart, setAddingToCart] = useState(new Set());
  
  // Filter and sort states - now controlled by sidebar
  const [sortOption, setSortOption] = useState('price_asc'); // Standardmäßig nach Preis aufsteigend sortieren (billigste zuerst)
  const [regionFilter, setRegionFilter] = useState('');
  const [territoryFilter, setTerritoryFilter] = useState('');
  const [rarityFilter, setRarityFilter] = useState('');
  const [resourceFilter, setResourceFilter] = useState('');
  const [plotStatusFilter, setPlotStatusFilter] = useState('');
  const [landTypeFilter, setLandTypeFilter] = useState('');
  const [terrainFilter, setTerrainFilter] = useState('');
  const [worksiteFilter, setWorksiteFilter] = useState('');
  const [priceRange, setPriceRange] = useState({ min: '', max: '' });
  
  // Unique filter options
  const [availableRegions, setAvailableRegions] = useState([]);
  const [availableTerritories, setAvailableTerritories] = useState([]);
  const [availableRarities, setAvailableRarities] = useState([]);
  const [availableResources, setAvailableResources] = useState([]);
  const [availablePlotStatuses, setAvailablePlotStatuses] = useState([]);
  const [availableLandTypes, setAvailableLandTypes] = useState([]);

  const sentinelRef = useRef(null);

  // Fetch land market data
  const fetchLandMarketData = useCallback(async (retryCount = 0) => {
    setLoading(true);
    setError(null);
    
    try {
      // Add timeout and headers for better reliability
      const response = await axios.get('https://vapi.splinterlands.com/land/deeds?status=market', {
        timeout: 10000, // 10 second timeout
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      
      // Handle different response formats
      let deeds = [];
      if (Array.isArray(response.data)) {
        deeds = response.data;
      } else if (response.data && response.data.data && Array.isArray(response.data.data.deeds)) {
        deeds = response.data.data.deeds;
      } else if (response.data && Array.isArray(response.data.deeds)) {
        deeds = response.data.deeds;
      } else if (response.data && Array.isArray(response.data.data)) {
        deeds = response.data.data;
      } else {
        deeds = [];
      }
      
      setLandDeeds(deeds);
      
      // Sort land deeds by price ascending (cheapest first) as default
      deeds.sort((a, b) => parseFloat(a.listing_price || 0) - parseFloat(b.listing_price || 0));
        
      // Extract unique filter options only if we have deeds
      if (deeds && deeds.length > 0) {
        // Use region_uid for filters instead of region_id for better accuracy
        const regions = [...new Set(deeds.map(deed => deed.region_uid || `R-${deed.region_id}`).filter(Boolean))].sort();
        const territories = [...new Set(deeds.map(deed => deed.territory).filter(Boolean))].sort();
        
        // Benutzerdefinierte Sortierreihenfolge für Raritäten
        const rarityOrder = { 'common': 1, 'rare': 2, 'epic': 3, 'legendary': 4, 'mythic': 5 };
        const rarities = [...new Set(deeds.map(deed => deed.rarity).filter(Boolean))].sort((a, b) => {
          return (rarityOrder[a.toLowerCase()] || 99) - (rarityOrder[b.toLowerCase()] || 99);
        });
        
        const resources = [...new Set(deeds.flatMap(deed => 
          deed.resource_type ? [deed.resource_type] : []
        ))].sort();
        
        // Extract plot statuses (natural, occupied, magical)
        const plotStatuses = [...new Set(deeds.map(deed => deed.plot_status).filter(Boolean))].sort();
        
        // Extract land types (normal land, keep, castle)
        const landTypes = [...new Set(deeds.map(deed => {
          if (deed.castle) return 'castle';
          if (deed.keep) return 'keep';
          return 'land';
        }))].sort();
        
        // Extract available resources from multiple sources: resource_id, resource_symbol, and land_stats
        const availableResourcesFromAPI = [...new Set(deeds.flatMap(deed => {
          const resourceTypes = [];
          
          // 1. Map resource_id to resource type names
          if (deed.resource_id) {
            const resourceIdMap = {
              1: 'grain',
              2: 'sps', 
              3: 'research',
              4: 'resource',
              5: 'wood',
              6: 'stone',
              7: 'iron',
              8: 'ore'
            };
            const resourceType = resourceIdMap[deed.resource_id];
            if (resourceType) {
              resourceTypes.push(resourceType);
            }
          }
          
          // 2. Add resource_symbol if present (normalize to lowercase)
          if (deed.resource_symbol) {
            resourceTypes.push(deed.resource_symbol.toLowerCase());
          }
          
          // 3. Legacy support: Add resource_type if present
          if (deed.resource_type) {
            resourceTypes.push(deed.resource_type.toLowerCase());
          }
          
          // 4. Add resources from land_stats if present
          if (deed.land_stats) {
            try {
              const landStats = typeof deed.land_stats === 'string' ? JSON.parse(deed.land_stats) : deed.land_stats;
              if (landStats.resources && Array.isArray(landStats.resources)) {
                resourceTypes.push(...landStats.resources.map(r => r.toLowerCase()));
              }
            } catch (e) {
              // Error parsing land_stats - skip this resource
            }
          }
          
          return resourceTypes;
        }).filter(Boolean))].sort();
        
        setAvailableRegions(regions);
        setAvailableTerritories(territories);
        setAvailableRarities(rarities);
        setAvailableResources(availableResourcesFromAPI);
        setAvailablePlotStatuses(plotStatuses);
        setAvailableLandTypes(landTypes);

        // Send filter data to sidebar
        window.dispatchEvent(new CustomEvent('landFilterDataUpdate', {
          detail: {
            availableRegions: regions,
            availableTerritories: territories,
            availableRarities: rarities,
            availableResources: resources,
            availableResourcesFromAPI: availableResourcesFromAPI,
            availablePlotStatuses: plotStatuses,
            availableLandTypes: landTypes,
            defaultSortOption: 'price_asc' // Die Standardsortierung an die Sidebar übermitteln
          }
        }));
      }
      
    } catch (err) {
      // Retry logic for network issues
      if (retryCount < 2 && (err.code === 'ECONNABORTED' || err.message.includes('timeout') || err.message.includes('Network Error'))) {
        setTimeout(() => {
          fetchLandMarketData(retryCount + 1);
        }, (retryCount + 1) * 2000);
        return;
      }
      
      // More detailed error logging
      if (err.response) {
        setError(`API Error ${err.response.status}: ${err.response.statusText || 'Failed to load land market data'}`);
      } else if (err.request) {
        setError('Network error: Unable to reach Splinterlands API. Please check your connection.');
      } else {
        setError(`Request error: ${err.message}`);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // Filter and sort deeds
  useEffect(() => {
    let filtered = [...landDeeds];

    // Apply filters
    if (regionFilter) {
      // Filter by region_uid for precise matching (e.g., 'PR-GLL-114')
      filtered = filtered.filter(deed => {
        const deedRegionUid = deed.region_uid || `R-${deed.region_id}`;
        return deedRegionUid === regionFilter;
      });
    }
    
    if (territoryFilter) {
      filtered = filtered.filter(deed => deed.territory === territoryFilter);
    }
    
    if (rarityFilter) {
      filtered = filtered.filter(deed => deed.rarity === rarityFilter);
    }
    
    if (resourceFilter) {
      filtered = filtered.filter(deed => {
        // Check multiple possible resource fields
        // 1. Check resource_symbol field (direct match)
        if (deed.resource_symbol && deed.resource_symbol.toLowerCase() === resourceFilter.toLowerCase()) {
          return true;
        }
        
        // 2. Check resource_id field (map filter to resource IDs)
        const resourceIdMap = {
          'grain': 1,
          'sps': 2, 
          'research': 3,
          'resource': 4,
          'wood': 5,
          'stone': 6,
          'iron': 7,
          'ore': 8
        };
        
        if (deed.resource_id && resourceIdMap[resourceFilter] === deed.resource_id) {
          return true;
        }
        
        // 3. Legacy support: check resource_type field
        if (deed.resource_type === resourceFilter) {
          return true;
        }
        
        // 4. Check land_stats.resources array
        if (deed.land_stats) {
          try {
            const landStats = typeof deed.land_stats === 'string' ? JSON.parse(deed.land_stats) : deed.land_stats;
            if (landStats.resources && Array.isArray(landStats.resources)) {
              return landStats.resources.some(resource => 
                resource && resource.toLowerCase() === resourceFilter.toLowerCase()
              );
            }
          } catch (e) {
            console.warn('Error parsing land_stats for resource filtering:', e);
          }
        }
        
        return false;
      });
    }
    
    if (plotStatusFilter) {
      filtered = filtered.filter(deed => deed.plot_status === plotStatusFilter);
    }
    
    if (landTypeFilter) {
      filtered = filtered.filter(deed => {
        if (landTypeFilter === 'castle') return deed.castle;
        if (landTypeFilter === 'keep') return deed.keep;
        if (landTypeFilter === 'land') return !deed.castle && !deed.keep;
        return true;
      });
    }

    if (terrainFilter) {
      filtered = filtered.filter(deed => deed.deed_type === terrainFilter);
    }

    if (worksiteFilter) {
      filtered = filtered.filter(deed => deed.worksite_type === worksiteFilter);
    }

    // Price range filter
    if (priceRange.min || priceRange.max) {
      filtered = filtered.filter(deed => {
        const price = parseFloat(deed.listing_price || 0);
        const min = priceRange.min ? parseFloat(priceRange.min) : 0;
        const max = priceRange.max ? parseFloat(priceRange.max) : Infinity;
        return price >= min && price <= max;
      });
    }

    // Apply sorting
    switch (sortOption) {
      case 'price_asc':
        filtered.sort((a, b) => parseFloat(a.listing_price || 0) - parseFloat(b.listing_price || 0));
        break;
      case 'price_desc':
        filtered.sort((a, b) => parseFloat(b.listing_price || 0) - parseFloat(a.listing_price || 0));
        break;
      case 'region_asc':
        filtered.sort((a, b) => (a.region_id || 0) - (b.region_id || 0));
        break;
      case 'region_desc':
        filtered.sort((a, b) => (b.region_id || 0) - (a.region_id || 0));
        break;
      case 'rarity_asc':
        filtered.sort((a, b) => (a.rarity || '').localeCompare(b.rarity || ''));
        break;
      case 'rarity_desc':
        filtered.sort((a, b) => (b.rarity || '').localeCompare(a.rarity || ''));
        break;
      default:
        // Default sort by price (cheapest first)
        filtered.sort((a, b) => parseFloat(a.listing_price || 0) - parseFloat(b.listing_price || 0));
        break;
    }

    setFilteredDeeds(filtered);
    setVisibleCount(50); // Reset visible count when filters change
  }, [landDeeds, regionFilter, territoryFilter, rarityFilter, resourceFilter, plotStatusFilter, landTypeFilter, terrainFilter, worksiteFilter, priceRange, sortOption]);

  // Load data on component mount
  useEffect(() => {
    fetchLandMarketData();
  }, [fetchLandMarketData]);

  // Listen for filter changes from sidebar
  useEffect(() => {
    const handleFilterChange = (event) => {
      const filters = event.detail;
      setSortOption(filters.sortOption || '');
      setRegionFilter(filters.regionFilter || '');
      setTerritoryFilter(filters.territoryFilter || '');
      setRarityFilter(filters.rarityFilter || '');
      setResourceFilter(filters.resourceFilter || '');
      setPlotStatusFilter(filters.plotStatusFilter || '');
      setLandTypeFilter(filters.landTypeFilter || '');
      setTerrainFilter(filters.terrainFilter || '');
      setWorksiteFilter(filters.worksiteFilter || '');
      setPriceRange(filters.priceRange || { min: '', max: '' });
    };

    window.addEventListener('landFiltersChanged', handleFilterChange);
    
    return () => {
      window.removeEventListener('landFiltersChanged', handleFilterChange);
    };
  }, []);

  // Infinite scroll effect
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && visibleCount < filteredDeeds.length) {
          setVisibleCount(prev => Math.min(prev + 50, filteredDeeds.length));
        }
      },
      { threshold: 0.1 }
    );

    if (sentinelRef.current) {
      observer.observe(sentinelRef.current);
    }

    return () => {
      if (sentinelRef.current) {
        observer.unobserve(sentinelRef.current);
      }
    };
  }, [visibleCount, filteredDeeds.length]);

  // Handle adding land deed to cart
  const handleAddToCart = async (deed) => {
    const deedId = deed.deed_uid || deed.uid || deed.id;
    if (addingToCart.has(deedId)) return;

    setAddingToCart(prev => new Set(prev).add(deedId));

    try {
      // Convert land deed to cart item format - optimiert für Land-Transaktionen
      const cartItem = {
        uid: deed.deed_uid || deed.uid || deed.id,
        listingItemId: deed.market_id, // Korrigiert: Verwende market_id für Land-Transaktionen
        market_listing_id: deed.market_listing_id,
        price: parseFloat(deed.listing_price || 0),
        buy_price: parseFloat(deed.listing_price || 0),
        originalPrice: parseFloat(deed.listing_price || 0),
        originalCurrency: deed.listing_currency || 'USD',
        currency: deed.listing_currency || 'USD',
        seller: deed.seller || deed.player,
        // Land-specific properties
        region_id: deed.region_id,
        tract_id: deed.tract_id, 
        plot_id: deed.plot_id,
        territory: deed.territory,
        map_name: deed.map_name,
        rarity: deed.rarity,
        resource_type: deed.resource_type,
        deed_type: deed.deed_type,
        land_stats: deed.land_stats
      };

      const landDetails = {
        name: `${deed.map_name || 'Land'} - R${deed.region_id} T${deed.tract_id} P${deed.plot_id}`,
        type: 'land_deed'
      };

      await addToLandCart(cartItem, landDetails);
    } catch (error) {
      console.error('Error adding land to cart:', error);
    } finally {
      setAddingToCart(prev => {
        const newSet = new Set(prev);
        newSet.delete(deed.deed_uid || deed.uid || deed.id);
        return newSet;
      });
    }
  };

  // Clear all filters
  // Helper functions
  const formatPrice = (price) => {
    const numPrice = parseFloat(price || 0);
    return numPrice.toLocaleString('en-US', { 
      style: 'currency', 
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const getRarityColor = (rarity) => {
    switch (rarity?.toLowerCase()) {
      case 'common': return colors.grey[500];
      case 'rare': return colors.blueAccent[500];
      case 'epic': return '#9c27b0'; // Violett für Epic
      case 'legendary': return '#ffd700'; // Gold für Legendary
      case 'mythic': return '#ff8c00'; // Orange für Mystic
      default: return colors.grey[400];
    }
  };

  const getTerritoryDisplay = (territory) => {
    if (!territory) return 'Unknown';
    return territory.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Generate full land ID in Splinterlands format: PR-GLL-114-05-016
  const getFullLandId = (deed) => {
    if (!deed) return 'Unknown';
    const regionUid = deed.region_uid || `R${deed.region_id || '?'}`;
    const tractId = String(deed.tract_id || 0).padStart(2, '0');
    const plotId = String(deed.plot_id || 0).padStart(3, '0');
    return `${regionUid}-${tractId}-${plotId}`;
  };

  const visibleDeeds = filteredDeeds.slice(0, visibleCount);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} sx={{ color: colors.greenAccent[400] }} />
        <Typography variant="h6" sx={{ ml: 2, color: colors.grey[100] }}>
          Loading land market data...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button 
          variant="contained" 
          onClick={fetchLandMarketData}
          sx={{ backgroundColor: colors.greenAccent[600] }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box m="20px">
      {/* Use the shared MarketHeader component */}
      <MarketHeader 
        onInfoClick={() => setShowInfo(true)}
        onLeaderboardClick={() => setShowLeaderboard(true)}
      />
      
      {/* Dialogs (managed by this component) */}
      <InfoDialog open={showInfo} onClose={() => setShowInfo(false)} />
      <LeaderboardDialog open={showLeaderboard} onClose={() => setShowLeaderboard(false)} />

      {/* Land Market Specific Content */}
 

      {/* Land Deeds Table with Larger Images */}
      <TableContainer component={Paper} 
        elevation={1}
        sx={{
          backgroundColor: colors.primary[400],
          border: `1px solid ${colors.primary[300]}`,
          borderRadius: 2,
          mb: 3
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: colors.primary[500] }}>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold', width: '200px' }}>Image & Type</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Location</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Territory</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Land Type</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Rarity</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Resource</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold' }}>Price</TableCell>
              <TableCell sx={{ color: colors.grey[100], fontWeight: 'bold', textAlign: 'center' }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleDeeds.map((deed, index) => {
              const deedId = deed.deed_uid || deed.uid || deed.id || index;
              
              // Terrain type mapping - extract to reusable function
              const getTerrainType = (deed) => {
                const deedType = (deed.deed_type || '').toLowerCase();
                const territory = (deed.territory || '').toLowerCase();
                
                // Priority 1: Map based on deed_type (most reliable)
                if (deedType.includes('desert')) return 'desert';
                if (deedType.includes('mountain')) return 'mountain';
                if (deedType.includes('forest')) return 'forest';
                if (deedType.includes('swamp')) return 'swamp';
                if (deedType.includes('hills')) return 'hills';
                if (deedType.includes('tundra')) return 'tundra';
                if (deedType.includes('canyon')) return 'canyon';
                if (deedType.includes('caldera')) return 'caldera';
                if (deedType.includes('river')) return 'river';
                if (deedType.includes('lake')) return 'lake';
                if (deedType.includes('badlands')) return 'badlands';
                if (deedType.includes('jungle')) return 'jungle';
                if (deedType.includes('bog')) return 'bog';
                if (deedType.includes('plains')) return 'plains';
                
                // Priority 2: Map based on territory names
                if (territory.includes('desert')) return 'desert';
                if (territory.includes('mountain')) return 'mountain';
                if (territory.includes('forest')) return 'forest';
                if (territory.includes('swamp')) return 'swamp';
                if (territory.includes('hills')) return 'hills';
                if (territory.includes('tundra')) return 'tundra';
                if (territory.includes('canyon')) return 'canyon';
                if (territory.includes('caldera') || territory.includes('fire')) return 'caldera';
                if (territory.includes('river')) return 'river';
                if (territory.includes('lake')) return 'lake';
                if (territory.includes('badlands')) return 'badlands';
                if (territory.includes('jungle')) return 'jungle';
                if (territory.includes('bog')) return 'bog';
                if (territory.includes('plains')) return 'plains';
                
                // Special territory mappings for known territories
                if (territory.includes('shimmering')) return 'desert'; // Shimmering Coasts = desert terrain
                if (territory.includes('coast')) return 'desert'; // Most coasts are desert terrain
                if (territory.includes('dunes')) return 'desert';
                if (territory.includes('peaks') || territory.includes('summit')) return 'mountain';
                if (territory.includes('grove') || territory.includes('woodlands')) return 'forest';
                if (territory.includes('bog') || territory.includes('marsh')) return 'bog';
                if (territory.includes('highlands') || territory.includes('ridge')) return 'hills';
                if (territory.includes('frozen') || territory.includes('ice')) return 'tundra';
                if (territory.includes('gorge') || territory.includes('ravine')) return 'canyon';
                if (territory.includes('volcanic') || territory.includes('lava')) return 'caldera';
                if (territory.includes('stream') || territory.includes('rapids')) return 'river';
                if (territory.includes('pond') || territory.includes('waters')) return 'lake';
                if (territory.includes('wastes') || territory.includes('barrens')) return 'badlands';
                if (territory.includes('rainforest') || territory.includes('tropical')) return 'jungle';
                if (territory.includes('wetlands') || territory.includes('mire')) return 'bog';
                if (territory.includes('grasslands') || territory.includes('meadows') || territory.includes('steppes')) return 'plains';
                
                // Default fallback to desert (most common)
                return 'desert';
              };
              
              // Enhanced URL generation with multiple fallback patterns for magical plots
              const getImageUrl = (deed) => {
                const normalize = str => (str || '').toLowerCase().replace(/ /g, '_').replace(/ä/g,'ae').replace(/ö/g,'oe').replace(/ü/g,'ue').replace(/ß/g,'ss');
                
                const terrainType = getTerrainType(deed);
                
                // Special handling for keeps and castles
                if (deed.castle) {
                  return `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_kingdom_castle.jpg`;
                }
                
                if (deed.keep) {
                  return `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_kingdom_keep.jpg`;
                }
                
                // Regular land deeds - use plot_status (natural, occupied, magical)
                if (!deed.rarity) return undefined;
                
                const status = deed.plot_status || 'natural'; // Default to natural if no status
                const rarity = normalize(deed.rarity);
                
                // For magical plots, try multiple URL patterns
                if (status === 'magical') {
                  const magicType = deed.magic_type?.toLowerCase();
                  
                  // Pattern 1: With magic_type (terrain_magical_magictype_rarity.jpg)
                  if (magicType && magicType !== 'neutral' && magicType !== '') {
                    const magicalUrlWithType = `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_magical_${magicType}_${rarity}.jpg`;
                    
                    console.log('Magical plot with magic_type detected:', {
                      deed_type: deed.deed_type,
                      territory: deed.territory,
                      terrainType,
                      magicType,
                      rarity,
                      url: magicalUrlWithType
                    });
                    
                    return magicalUrlWithType;
                  }
                  
                  // Pattern 2: Without magic_type (terrain_magical_rarity.jpg)
                  const magicalUrlBasic = `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_magical_${rarity}.jpg`;
                  
                  console.log('Magical plot without magic_type detected:', {
                    deed_type: deed.deed_type,
                    territory: deed.territory,
                    terrainType,
                    magicType: deed.magic_type || 'none',
                    rarity,
                    url: magicalUrlBasic
                  });
                  
                  return magicalUrlBasic;
                }
                
                // Standard pattern for natural and occupied plots
                const finalUrl = `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_${status}_${rarity}.jpg`;
                
                return finalUrl;
              };
              
              const imgUrl = getImageUrl(deed);
              return (
                <TableRow 
                  key={deedId}
                  sx={{ 
                    '&:nth-of-type(odd)': { backgroundColor: colors.primary[450] },
                    '&:hover': { backgroundColor: colors.primary[350] },
                    height: '120px' // Increase row height for larger images
                  }}
                >
                  <TableCell sx={{ width: '200px', p: 1 }}>
                    <Box display="flex" alignItems="center" gap={2}>
                      {imgUrl && (
                        <img 
                          src={imgUrl} 
                          alt={deed.deed_type} 
                          style={{ 
                            width: 160, 
                            height: 90, 
                            objectFit: 'cover', 
                            borderRadius: 8, 
                            border: `2px solid ${getRarityColor(deed.rarity)}`,
                            boxShadow: '0 4px 8px rgba(0,0,0,0.2)'
                          }} 
                          onError={(e) => { 
                            // Enhanced fallback logic for magical plots
                            const currentSrc = e.target.src;
                            
                            if (deed.plot_status === 'magical') {
                              const terrainType = getTerrainType(deed);
                              const rarity = deed.rarity?.toLowerCase();
                              const magicType = deed.magic_type?.toLowerCase() || 'neutral';
                              
                              // Try different magical naming patterns as fallbacks
                              if (currentSrc.includes('_magical_') && currentSrc.includes(`_${magicType}_`)) {
                                // Try without magic type (older pattern)
                                e.target.src = `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_magical_${rarity}.jpg`;
                              } else if (currentSrc.includes('_magical_') && !currentSrc.includes(`_${magicType}_`)) {
                                // Try just terrain and rarity
                                e.target.src = `https://next.splinterlands.com/assets/lands/deedsSurveyed/${terrainType}_${rarity}.jpg`;
                              } else {
                                // Final fallback - hide the image
                                e.target.style.display = 'none';
                                console.log('All magical plot image patterns failed for:', {
                                  deed_id: deed.deed_uid,
                                  terrain: terrainType,
                                  magic_type: magicType,
                                  rarity: rarity,
                                  last_url: currentSrc
                                });
                              }
                            } else {
                              // For non-magical plots, just hide on error
                              e.target.style.display = 'none';
                            }
                          }} 
                        />
                      )}
                      <Box>
                        <Typography variant="body2" color={colors.grey[200]} fontWeight="bold">
                          {deed.deed_type ? deed.deed_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : '-'}
                        </Typography>
                        {deed.castle && (
                          <Chip 
                            label="Castle" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#ffd700', 
                              color: 'black', 
                              fontSize: '0.7rem',
                              mt: 0.5
                            }} 
                          />
                        )}
                        {deed.keep && (
                          <Chip 
                            label="Keep" 
                            size="small" 
                            sx={{ 
                              backgroundColor: '#ff8c00', 
                              color: 'white', 
                              fontSize: '0.7rem',
                              mt: 0.5
                            }} 
                          />
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2" color={colors.grey[100]} fontWeight="bold">
                        {deed.map_name || 'Unknown Map'}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                        <LocationOnIcon sx={{ color: colors.grey[400], fontSize: '0.9rem' }} />
                        <Typography variant="caption" color={colors.grey[300]} fontWeight="medium">
                          {getFullLandId(deed)}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color={colors.grey[200]}>
                      {getTerritoryDisplay(deed.territory)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={deed.plot_status === 'magical' ? '#9c27b0' : deed.plot_status === 'occupied' ? colors.redAccent[400] : colors.greenAccent[400]}
                      fontWeight="bold"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {deed.plot_status || 'Natural'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography 
                      variant="body2" 
                      color={deed.castle ? '#ffd700' : deed.keep ? '#ff8c00' : colors.grey[300]}
                      fontWeight="bold"
                      sx={{ textTransform: 'capitalize' }}
                    >
                      {deed.castle ? 'Castle' : deed.keep ? 'Keep' : 'Land'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {deed.rarity ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <DiamondIcon sx={{ color: getRarityColor(deed.rarity), fontSize: '1rem' }} />
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: getRarityColor(deed.rarity),
                            fontWeight: 'bold',
                            textTransform: 'capitalize'
                          }}
                        >
                          {deed.rarity}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color={colors.grey[400]}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {deed.resource_type ? (
                      <Box display="flex" alignItems="center" gap={1}>
                        <GrainIcon sx={{ color: colors.greenAccent[400], fontSize: '1rem' }} />
                        <Typography variant="body2" color={colors.greenAccent[300]}>
                          {deed.resource_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color={colors.grey[400]}>-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="h6" color={colors.greenAccent[400]} fontWeight="bold">
                      {formatPrice(deed.listing_price)}
                    </Typography>
                    <Typography variant="caption" color={colors.grey[400]}>
                      {deed.listing_currency || 'USD'}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title="Add to Cart">
                      <IconButton
                        onClick={() => handleAddToCart(deed)}
                        disabled={addingToCart.has(deedId)}
                        sx={{
                          color: colors.greenAccent[400],
                          '&:hover': {
                            backgroundColor: colors.greenAccent[800],
                            color: colors.greenAccent[200]
                          },
                          '&:disabled': {
                            color: colors.grey[600]
                          }
                        }}
                      >
                        {addingToCart.has(deedId) ? (
                          <CircularProgress size={20} sx={{ color: colors.grey[400] }} />
                        ) : (
                          <ShoppingCartIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Infinite Scroll Sentinel */}
      {visibleCount < filteredDeeds.length && (
        <Box textAlign="center" py={2}>
          <div ref={sentinelRef} style={{ height: 1 }} />
          <Typography variant="body2" color={colors.grey[400]}>
            Showing {visibleCount} of {filteredDeeds.length} items
          </Typography>
        </Box>
      )}

      {/* No Results Message */}
      {filteredDeeds.length === 0 && !loading && (
        <Paper 
          elevation={1}
          sx={{
            p: 4,
            textAlign: 'center',
            backgroundColor: colors.primary[400],
            borderRadius: 2,
            border: `1px solid ${colors.primary[300]}`
          }}
        >
          <TerrainIcon sx={{ fontSize: '4rem', color: colors.grey[400], mb: 2 }} />
          <Typography variant="h5" color={colors.grey[300]} sx={{ mb: 1 }}>
            No land deeds found
          </Typography>
          <Typography variant="body1" color={colors.grey[400]}>
            Try adjusting your filters or check back later for new listings.
          </Typography>
        </Paper>
      )}
      
      {/* Mobile Filter Button */}
      {isMobile && (
        <Fab
          color="primary"
          aria-label="filter"
          onClick={() => setIsSidebar(true)}
          sx={{
            position: 'fixed',
            bottom: 20,
            left: 20,
            zIndex: 1000,
            backgroundColor: colors.blueAccent[600],
            '&:hover': {
              backgroundColor: colors.blueAccent[700]
            }
          }}
        >
          <FilterListIcon />
        </Fab>
      )}
    </Box>
  );
};

export default LandMarket;
